"use client";

import React, { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import OtpInput from "./OtpInput";

export type InactiveAccountModalProps = {
  isOpen: boolean;
  userEmail?: string;
  initialCodeSent?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
};

export default function InactiveAccountModal({
  isOpen,
  userEmail,
  initialCodeSent = false,
  onClose,
  onSuccess,
}: InactiveAccountModalProps) {
  const t = useTranslations("inactiveAccount");
  const {
    sendingCode,
    verifying,
    otp,
    setOtp,
    timer,
    error,
    sendCode,
    verifyCode,
    logout,
  } = useEmailVerification(userEmail, initialCodeSent, onSuccess);

  const modalContainerRef = useRef<HTMLDivElement | null>(null);

  // Focus trap inside modal & Escape key handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (onClose) {
          e.preventDefault();
          onClose();
        }
        return;
      }

      if (e.key === "Tab" && modalContainerRef.current) {
        const focusables = modalContainerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-backdrop p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactive-account-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div
        ref={modalContainerRef}
        className="relative w-full max-w-md overflow-hidden rounded-[32px] bg-modal p-6 sm:p-8 shadow-2xl border border-border-subtle animate-in zoom-in-95 duration-200 text-center"
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-5 sm:right-5 flex h-9 w-9 items-center justify-center rounded-full bg-hover text-text-muted hover:bg-selected hover:text-foreground transition cursor-pointer"
          >
            &times;
          </button>
        )}
        {/* Email Icon / Banner */}
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          <svg
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <h2 id="inactive-account-title" className="mb-2 text-2xl font-extrabold text-primary">
          {t("title")}
        </h2>

        <p className="mb-4 text-sm leading-relaxed text-text-secondary">
          {t("description")}
        </p>

        {userEmail && (
          <div className="mb-6 rounded-2xl bg-primary/5 border border-primary/10 p-3.5">
            <span className="block text-xs text-text-muted font-medium mb-1">
              {t("email_label")}
            </span>
            <span className="block text-sm font-bold text-primary break-all dir-ltr" dir="ltr">
              {userEmail}
            </span>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-2xl bg-danger/10 border border-danger/20 p-3 text-xs font-semibold text-danger animate-in fade-in">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="mb-3 block text-xs font-bold uppercase tracking-wider text-text-muted">
              {t("enter_code")}
            </label>
            <OtpInput
              value={otp}
              onChange={setOtp}
              disabled={verifying}
              onComplete={(code) => verifyCode(code)}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-3">
            {/* Verify — primary action */}
            <button
              type="button"
              disabled={verifying || otp.length !== 6}
              onClick={() => verifyCode()}
              className="w-full rounded-2xl bg-primary py-3.5 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <svg className="h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>{t("verifying")}</span>
                </>
              ) : (
                t("verify_account")
              )}
            </button>

            {/* Send / Resend verification email — modal & OTP stay visible */}
            <button
              type="button"
              disabled={sendingCode || timer > 0}
              onClick={sendCode}
              className="w-full rounded-2xl border border-border bg-surface py-3.5 text-sm font-bold text-foreground transition hover:bg-hover focus:outline-none focus:ring-2 focus:ring-focus-ring disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
              {sendingCode ? (
                <>
                  <svg className="h-4 w-4 animate-spin text-current" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>{t("sending_code")}</span>
                </>
              ) : timer > 0 ? (
                t("resend_code_timer", { timer })
              ) : (
                t("send_code")
              )}
            </button>

            {/* Logout */}
            <button
              type="button"
              onClick={logout}
              className="w-full rounded-2xl py-3 text-sm font-bold text-text-muted hover:text-foreground transition focus:outline-none focus:ring-2 focus:ring-focus-ring cursor-pointer"
            >
              {t("logout")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
